import { Chip } from "@/components/core/Chip";
import { HoverTooltip } from "@/components/core/HoverTooltip";
import { Button } from "@/components/core/buttons/Button";
import { PersonalFeedChipIcon } from "@/lib/icons";
import { ExtensionLink } from "components/core/ExtensionLink";
import { ContentCard } from "components/domain/contents/cards/ContentCard";
import {
    SectionHeader,
    SectionTitle
} from "components/domain/contents/sections";
import { AnimatePresence, motion } from "framer-motion";
import {
    Mix_Content_Tag_Enum_Enum,
    useAllTimePopularContentsQuery
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { fireEvent } from "lib/ga";
import { RotateCw, SparklesIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ContentSection } from "types/app";

export const PersonalFeedSection = ({
    contentSection,
    tags,
    className,
    ...props
}: {
    contentSection: ContentSection;
    tags: Mix_Content_Tag_Enum_Enum[] | null;
} & { className?: string }) => {
    const { isLoggedIn } = useAuth();

    // Track offset and rotation cycle for content rotation
    const [offset, setOffset] = useState(0);
    const [rotationCycle, setRotationCycle] = useState(0);
    // Track shown content IDs to avoid duplicates
    const [shownContentIds, setShownContentIds] = useState<Set<string>>(
        new Set()
    );

    // Auto-refresh timer state
    const [progress, setProgress] = useState(0);
    const [isRotating, setIsRotating] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Query all-time popular content with larger pool
    const { data, loading, refetch } = useAllTimePopularContentsQuery({
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        variables: {
            limit: 50, // Increased from 15 to get more variety
            minViews: 10
        }
    });

    // Shuffle array helper
    const shuffleArray = <T,>(array: T[]): T[] => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            const temp = shuffled[i]!;
            shuffled[i] = shuffled[j]!;
            shuffled[j] = temp;
        }
        return shuffled;
    };

    // Filter and limit content based on user's tags
    const contents = useMemo(() => {
        if (!data?.mix_content_popular_all_time) return [];

        // Extract content from the popular table and filter out original content
        const allContents = data.mix_content_popular_all_time
            .map((item) => item.content)
            .filter(
                (content): content is NonNullable<typeof content> =>
                    Boolean(content) && content.source !== "original"
            );

        // If no tags filter, use all contents
        let poolContents =
            !tags || tags.length === 0
                ? allContents
                : allContents.filter((content) => {
                      const contentTags = content.mix_content_tags?.map(
                          (t) => t.mix_content_tag_enum?.value
                      );
                      return contentTags?.some((tag) =>
                          tags.includes(tag as Mix_Content_Tag_Enum_Enum)
                      );
                  });

        // Filter out already shown content
        let availableContents = poolContents.filter(
            (content) => !shownContentIds.has(content.id)
        );

        // If we've exhausted all content, recycle by resetting the shown pool
        if (availableContents.length === 0 && poolContents.length > 0) {
            // Reset shown content IDs to allow recycling
            setShownContentIds(new Set());
            availableContents = poolContents;
        }

        // If we still have no content at all, return empty array
        if (availableContents.length === 0) {
            return [];
        }

        // If pool is too small, return what we have
        if (availableContents.length <= 2) {
            return availableContents;
        }

        // Shuffle pool to create variety
        const shuffled = shuffleArray(availableContents);

        // Take first 2 items from shuffled pool
        return shuffled.slice(0, 2);
    }, [data, tags, shownContentIds, rotationCycle]);

    // Helper function to determine content type
    const getContentType = (content: any) => {
        if (content.isoriginal) {
            return content.released ? "original" : "upcoming";
        }
        return "curation";
    };

    // Start auto-refresh timer
    const startTimer = () => {
        // Clear any existing timers
        if (timerRef.current) clearTimeout(timerRef.current);
        if (progressIntervalRef.current)
            clearInterval(progressIntervalRef.current);

        // Reset progress
        setProgress(0);

        // Update progress every 100ms (70 steps for 7 seconds)
        progressIntervalRef.current = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) return 100;
                return prev + 100 / 70; // 7000ms / 100ms = 70 steps
            });
        }, 100);

        // Trigger rotation and refresh after 7 seconds
        timerRef.current = setTimeout(() => {
            setIsRotating(true);
            handleReload();
            setTimeout(() => setIsRotating(false), 500); // Match rotation duration
        }, 7000);
    };

    // Cleanup timers on unmount
    useEffect(() => {
        // Start timer on mount
        startTimer();

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (progressIntervalRef.current)
                clearInterval(progressIntervalRef.current);
        };
    }, []);

    // Handle reload button click
    const handleReload = () => {
        // Mark current content as shown
        contents.forEach((content) => {
            setShownContentIds((prev) => {
                const newSet = new Set(prev);
                newSet.add(content.id);
                return newSet;
            });
        });

        // Check if we've exhausted the available pool
        if (data?.mix_content_popular_all_time) {
            const allContentIds = data.mix_content_popular_all_time
                .map((item) => item.content?.id)
                .filter((id): id is string => Boolean(id));

            const availableCount = allContentIds.filter(
                (id) => !shownContentIds.has(id)
            ).length;

            // If we have fewer than 2 unseen items, reset the shown pool
            if (availableCount < 2) {
                setShownContentIds(new Set());
                // Refetch to potentially get fresh data from server
                refetch();
            }
        }

        // Increment rotation cycle to trigger reshuffle
        setRotationCycle((prev) => prev + 1);

        // Restart timer after manual or auto refresh
        startTimer();
    };

    // Always render the section - content recycling ensures we always have items
    return (
        <div className={className} {...props}>
            <SectionHeader
                className="absolute -top-12 flex w-full items-center
                    justify-between"
            >
                <div className="flex items-center gap-2">
                    <SectionTitle>추천 콘텐츠</SectionTitle>
                    <button
                        onClick={() => {
                            // GA4 tracking for manual refresh button click
                            fireEvent({
                                event: "personal_feed_refresh_click"
                            });
                            handleReload();
                        }}
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                        className="group relative rounded-full p-1
                            transition-all hover:bg-gray-100
                            dark:hover:bg-gray-800"
                        aria-label="새로운 콘텐츠 보기"
                        style={{ width: "36px", height: "36px" }}
                    >
                        {/* Progress ring */}
                        <svg
                            className="absolute left-0 top-0"
                            width="36"
                            height="36"
                            style={{ transform: "rotate(-90deg)" }}
                        >
                            <circle
                                cx="18"
                                cy="18"
                                r="16"
                                fill="none"
                                stroke="#638FFF"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeDasharray={`${2 * Math.PI * 16}`}
                                strokeDashoffset={`${2 * Math.PI * 16 * (1 - progress / 100)}`}
                                style={{
                                    transition: "stroke-dashoffset 0.1s linear"
                                }}
                            />
                        </svg>

                        <RotateCw
                            className="size-4 text-gray-600 dark:text-gray-400"
                            style={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                transform: `translate(-50%, -50%) rotate(${isRotating || isHovering ? "180deg" : "0deg"})`,
                                transition: "transform 0.5s ease-in-out"
                            }}
                        />
                    </button>
                </div>
                {/* <SectionControls> */}
                {/*     <ExtensionLink href="#"> */}
                {/*         <Button */}
                {/*             size="small" */}
                {/*             color="grey" */}
                {/*             variant="text" */}
                {/*             endIcon="ChevronRight" */}
                {/*         > */}
                {/*             더보기 */}
                {/*         </Button> */}
                {/*     </ExtensionLink> */}
                {/* </SectionControls> */}
            </SectionHeader>

            <AnimatePresence mode="wait">
                {contents.map((content, index) => (
                    <motion.div
                        key={`${content.id}-${rotationCycle}-${offset}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.3 }}
                        className="relative"
                    >
                        <div className="absolute z-10 p-[16px]">
                            <div className="group relative cursor-help">
                                <HoverTooltip
                                    tooltipClassName="bottom-[-37px] left-[5.5rem] -translate-x-1/2 before:left-1/2 before:-translate-x-[4rem]"
                                    content="사용자 클릭 기반으로 추천했어요!"
                                >
                                    <div
                                        className="flex h-[23px] w-[56px]
                                            items-center rounded-[9px] bg-white
                                            px-[3px] shadow-md"
                                    >
                                        <PersonalFeedChipIcon
                                            className="![&_*]:fill-blue-400
                                                size-6 scale-[0.8]
                                                text-blue-400"
                                        />
                                        <p className="text-xs text-black">
                                            추천
                                        </p>
                                    </div>
                                </HoverTooltip>
                            </div>
                        </div>
                        <ContentCard
                            type={getContentType(content)}
                            content={content}
                            priority={index === 0} // First card is above-the-fold
                            onClick={() => {
                                const params = {
                                    content_section: contentSection,
                                    content_url: content.originalurl,
                                    content_isoriginal: content.isoriginal,
                                    content_released: content.released
                                };

                                fireEvent({
                                    event: "content_click",
                                    params: {
                                        content_id: content.id,
                                        content_title: content.title,
                                        ...params
                                    }
                                });
                            }}
                        />
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};
